// ========= CONTACTS S3 CONFIG =========
const CONTACTS_BUCKET_NAME = process.env.REACT_APP_CAMPAIGN_MEDIA_BUCKET_NAME
const REGION = process.env.REACT_APP_AWS_REGION || "me-central-1"
const CONTACTS_PATH_NAME = process.env.REACT_APP_CONTACTS_PATH_NAME

const getConfig = () => {
  return {
    bucketName: CONTACTS_BUCKET_NAME,
    region: REGION,
    bucketUrl: `https://${CONTACTS_BUCKET_NAME}.s3.${REGION}.amazonaws.com`,
  }
}

export const CONTACTS_S3_CONFIG = new Proxy(
  {},
  {
    get(target, prop) {
      const config = getConfig()
      return config[prop]
    },
  },
)

const pad = (n) => String(n).padStart(2, "0")

export const generateTimestampId = () => {
  const d = new Date()
  // Format: YYYYMMDDHHmmssSSS + random 2 digits
  const timestamp =
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}` +
    String(d.getMilliseconds()).padStart(3, "0")
  const random = String(Math.floor(Math.random() * 100)).padStart(2, "0")
  return timestamp + random
}

export const getFileExtension = (filename, fallback = "xlsx") => {
  const lastDot = (filename || "").lastIndexOf(".")
  return lastDot === -1 ? fallback : filename.slice(lastDot + 1).toLowerCase()
}

// ========= VALIDATION =========
export const validateExcelFile = (file, maxSizeMB = 10) => {
  if (!file) throw new Error("No file provided")
  const validTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "text/csv",
  ]
  const validExtensions = [".xls", ".xlsx", ".xlsm", ".csv"]
  const fileExtension = getFileExtension(file.name, "")

  const isValidType =
    validTypes.includes(file.type) ||
    validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

  if (!isValidType) {
    throw new Error("Only Excel files (.xls, .xlsx, .xlsm, .csv) are allowed")
  }
  if (file.size > maxSizeMB * 1024 * 1024) {
    throw new Error(`File size must be less than ${maxSizeMB}MB`)
  }
  return true
}

// ========= EXCEL FILE UPLOAD =========
export async function uploadContactExcelToS3(file, contactId) {
  validateExcelFile(file)

  if (!contactId) {
    throw new Error("Contact ID is required for uploading Excel file")
  }

  // Get file extension
  const fileExtension = getFileExtension(file.name, "xlsx")

  // Build timestamp-based S3 key so each uploaded sheet is unique
  const folderPrefix = CONTACTS_PATH_NAME?.replace(/^\//, "") || "contact_stage"
  const timestampId = generateTimestampId()
  const fileKey = `${folderPrefix}/${contactId}_${timestampId}.${fileExtension}`

  const uploadUrl = `${CONTACTS_S3_CONFIG.bucketUrl}/${fileKey}`

  const resp = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type":
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  })

  if (!resp.ok) {
    const txt = await resp.text().catch(() => "")
    throw new Error(`Upload failed ${resp.status}: ${txt || resp.statusText}`)
  }

  return { key: fileKey, url: getContactPublicUrl(fileKey) }
}

export const getContactPublicUrl = (key) => {
  const config = getConfig()
  // Ensure key starts with / if it doesn't already
  const normalizedKey = key.startsWith("/") ? key : `/${key}`
  return `${config.bucketUrl}${normalizedKey}`
}
